import React, { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import Table from '@cloudscape-design/components/table';
import FormField from '@cloudscape-design/components/form-field';
import Box from '@cloudscape-design/components/box';
import TextFilter from '@cloudscape-design/components/text-filter';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import { ResponseViewer } from '../components/ResponseViewer';

interface ApplicationsPageProps {
  apps: { name: string }[];
  onAppsChange: (apps: { name: string }[]) => void;
}

export const ApplicationsPage: React.FC<ApplicationsPageProps> = ({ apps, onAppsChange }) => {
  const [name, setName] = useState('');
  const [filterText, setFilterText] = useState('');
  const { loading, result, execute } = useApi();

  const load = () => {
    api('GET', '/applications').then(d => { if (Array.isArray(d)) onAppsChange(d); }).catch(() => {});
  };

  const create = () => execute(async () => {
    const r = await api('POST', '/application', { name });
    setName('');
    load();
    return r;
  });

  const remove = (n: string) => execute(async () => {
    const r = await api('DELETE', `/application/${encodeURIComponent(n)}`);
    load();
    return r;
  });

  const filtered = apps.filter(a =>
    !filterText || a.name.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Applications</Header>

      <Container header={<Header variant="h2">Create Application</Header>}>
        <SpaceBetween direction="horizontal" size="s">
          <FormField label="Name">
            <Input value={name} onChange={({ detail }) => setName(detail.value)} placeholder="e.g. yeti_bom_2" />
          </FormField>
          <Box padding={{ top: 'xxl' }}>
            <Button variant="primary" onClick={create} loading={loading} disabled={!name}>
              Create
            </Button>
          </Box>
        </SpaceBetween>
      </Container>

      <Table
        header={<Header counter={`(${filtered.length})`}>All Applications</Header>}
        filter={
          <TextFilter
            filteringText={filterText}
            onChange={({ detail }) => setFilterText(detail.filteringText)}
            filteringPlaceholder="Find applications"
          />
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: item => item.name,
            sortingField: 'name',
          },
          {
            id: 'actions',
            header: 'Actions',
            width: 120,
            cell: item => (
              <Button variant="inline-link" onClick={() => remove(item.name)}>Delete</Button>
            ),
          },
        ]}
        items={filtered}
        sortingDisabled={false}
        empty={<Box textAlign="center" padding="l">No applications found</Box>}
      />

      <ResponseViewer data={result?.data} error={result?.error} />
    </SpaceBetween>
  );
};
